import * as React from "react";

// Observed vs expected counts of instances by how many of their runs passed:
// a solid bar for what happened, a tick for what independent runs at the same
// overall rate would give. Bars pushed to the ends and a hollow middle mean
// the runs of one instance agree with each other (clustering).
//
// Props match the MDX registry entry:
// `observed` instances per pass count, index k = instances with k passes
// `expected` same length, fractional counts allowed (e.g. a binomial)
// `runs`     runs per instance; rows are k = 0..runs (clamped to 0..20)
//
// Missing or non-finite entries count as 0.

export interface ClumpBarsProps {
  observed: number[];
  expected: number[];
  runs: number;
}

const MAX_RUNS = 20;
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 0);
const fmt = (n: number) => Number(n.toFixed(1)).toString();

export function ClumpBars({ observed, expected, runs }: ClumpBarsProps) {
  const labelId = React.useId();
  const nRuns = Number.isFinite(runs) ? Math.min(Math.max(0, Math.floor(runs)), MAX_RUNS) : 0;
  const rows = Array.from({ length: nRuns + 1 }, (_, k) => ({ k, obs: num(observed?.[k]), exp: num(expected?.[k]) }));
  const max = Math.max(1, ...rows.map((r) => Math.max(r.obs, r.exp)));
  const total = rows.reduce((a, r) => a + r.obs, 0);

  return (
    <div className="gc-widget cb-widget">
      <div className="gc-widget-label" id={labelId}>
        <span aria-hidden="true">// </span>instances by passes out of {nRuns} — {fmt(total)} instances
      </div>
      <ol className="cb-list" role="list" aria-labelledby={labelId}>
        {rows.map((r) => (
          <li key={r.k} className="cb-row" role="listitem">
            <span className="viz-sr">
              {r.k} of {nRuns} runs passed: {fmt(r.obs)} observed, {fmt(r.exp)} expected if runs were independent
            </span>
            <span className="cb-name" aria-hidden="true">
              {r.k} of {nRuns}
            </span>
            <div className="cb-track" aria-hidden="true">
              <div className="cb-bar" style={{ width: `${(r.obs / max) * 100}%` }} />
              <div className="cb-exp" style={{ left: `${(r.exp / max) * 100}%` }} />
            </div>
            <span className="cb-val" aria-hidden="true">
              {fmt(r.obs)} <span className="cb-vs">vs {fmt(r.exp)}</span>
            </span>
          </li>
        ))}
      </ol>
      <div className="gc-legend" aria-hidden="true">
        <span>▮ observed instances</span>
        <span>┃ expected if the runs were independent</span>
      </div>
    </div>
  );
}

export default ClumpBars;
